import React from "react";
import { Button } from "reactstrap";

/**
 * Previous / next, and an honest count.
 *
 * Both research lists used to ask for up to 100 rows and render whatever came
 * back, with a footer reading "100 shared forecasts". That number was the page
 * size wearing a total's clothing: there were 107 rows, and the six oldest were
 * unreachable by any route other than knowing their id. A whole ETH question
 * existed on the site and could not be found by browsing to it.
 *
 * So the count here is always the server's `total` for the current filter, and
 * it is stated as a range — "Showing 26–50 of 107" — which cannot be misread as
 * a total the way a bare "25" could.
 *
 * The page lives in the URL, so a link to page 3 of a filtered list is a link
 * to page 3 of that list. Page 1 leaves the parameter out, keeping the common
 * URL clean.
 *
 * Rendered even when everything fits on one page: the sentence explaining what
 * a row is belongs under the table either way, and a control that appears and
 * disappears as you filter is harder to trust than one that greys out. Only the
 * two buttons are conditional.
 */
const Pagination = ({
  page, totalPages, total, pageSize, onPage, loading = false,
  noun = "forecast", children,
}) => {
  if (!total) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const many = totalPages > 1;

  return (
    <div className="mt-3">
      {many && (
        <div className="d-flex justify-content-center align-items-center gap-3 mb-2">
          <Button
            size="sm"
            color="light"
            disabled={page <= 1 || loading}
            onClick={() => onPage(page - 1)}
          >
            ← Previous
          </Button>
          <span className="text-muted fs-13">
            Page {page} of {totalPages}
          </span>
          <Button
            size="sm"
            color="light"
            disabled={page >= totalPages || loading}
            onClick={() => onPage(page + 1)}
          >
            Next →
          </Button>
        </div>
      )}
      <p className="text-muted fs-13 mb-0 text-center">
        {many
          ? `Showing ${from}–${to} of ${total} ${noun}${total === 1 ? "" : "s"}.`
          : `${total} ${noun}${total === 1 ? "" : "s"}.`}
        {children ? " " : ""}
        {children}
      </p>
    </div>
  );
};

export default Pagination;
